import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import {
  User, Heart, Phone, Shield, Pill, Activity, ClipboardList, Building2,
  Plus, Download, ChevronDown, AlertTriangle, CheckCircle2, Clock,
  Utensils, PersonStanding, Droplets, Brain, Moon, FileText, Star, Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const DL_ICONS = { nutrition_hydration: Utensils, mobility: PersonStanding, continence: Droplets, skin_integrity: Shield, personal_care: User, mental_wellbeing: Brain, behaviour_support: AlertTriangle, sleep: Moon };
const DL_LABELS = { nutrition_hydration: 'Nutrition & Hydration', mobility: 'Mobility', continence: 'Continence', skin_integrity: 'Skin Integrity', personal_care: 'Personal Care', mental_wellbeing: 'Mental Wellbeing', behaviour_support: 'Behaviour Support', sleep: 'Sleep' };

function Section({ title, icon: Icon, count, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 p-4 bg-muted/30 hover:bg-muted/50 transition-colors text-left">
        <Icon className="w-4 h-4 text-[#00A6A0]" />
        <span className="text-sm font-medium text-foreground flex-1">{title}</span>
        {count != null && <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{count}</span>}
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && <div className="p-4 space-y-3">{children}</div>}
    </div>
  );
}

export default function ClientRecord() {
  const { clientId } = useParams();
  const { user } = useAuth();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [dlCategories, setDlCategories] = useState({});
  const [dlDialog, setDlDialog] = useState(false);
  const [dlForm, setDlForm] = useState({ category: '', coded_status: '', observation: '', concern_raised: false });
  const [hospDialog, setHospDialog] = useState(false);
  const [hospForm, setHospForm] = useState({ event_type: 'admission', hospital_name: '', reason: '', admission_date: '' });
  const [profDialog, setProfDialog] = useState(false);
  const [profForm, setProfForm] = useState({ professional_name: '', role: 'gp', organization_name: '', phone: '' });
  const [fbDialog, setFbDialog] = useState(false);
  const [fbForm, setFbForm] = useState({ type: 'compliment', source: 'family', source_name: '', description: '' });

  const isAdmin = ['agency_admin', 'ceo'].includes(user?.role);
  const canWriteDL = ['agency_admin', 'ceo', 'caregiver'].includes(user?.role);

  useEffect(() => { if (clientId) { fetchRecord(); fetchDLCats(); } }, [clientId]);

  const fetchRecord = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/dscr/record/${clientId}`, { withCredentials: true });
      setRecord(data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to load record'); }
    finally { setLoading(false); }
  };

  const fetchDLCats = async () => {
    try { const { data } = await axios.get(`${API}/api/dscr/reference/daily-living-categories`, { withCredentials: true }); setDlCategories(data); } catch { /* ref data */ }
  };

  const saveDailyLiving = async () => {
    if (!dlForm.category || !dlForm.coded_status) return toast.error('Select category and status');
    try {
      await axios.post(`${API}/api/dscr/daily-living`, { client_id: clientId, ...dlForm }, { withCredentials: true });
      toast.success('Observation recorded'); setDlDialog(false); fetchRecord();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const saveHospital = async () => {
    if (!hospForm.hospital_name || !hospForm.admission_date) return toast.error('Hospital name and date required');
    try {
      await axios.post(`${API}/api/dscr/hospital-events`, { client_id: clientId, ...hospForm }, { withCredentials: true });
      toast.success('Hospital event recorded'); setHospDialog(false); fetchRecord();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const saveProfessional = async () => {
    if (!profForm.professional_name) return toast.error('Name required');
    try {
      await axios.post(`${API}/api/dscr/professionals`, { client_id: clientId, ...profForm }, { withCredentials: true });
      toast.success('Professional added'); setProfDialog(false); fetchRecord();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const saveFeedback = async () => {
    if (!fbForm.description) return toast.error('Description required');
    try {
      await axios.post(`${API}/api/dscr/feedback`, { client_id: clientId, ...fbForm }, { withCredentials: true });
      toast.success('Feedback recorded'); setFbDialog(false); fetchRecord();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const downloadFHIR = async () => {
    try {
      const { data } = await axios.get(`${API}/api/dscr/record/${clientId}/fhir`, { withCredentials: true });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/fhir+json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `DSCR_FHIR_${record?.person?.name?.replace(/\s/g, '_')}.json`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success('FHIR R4 Bundle exported');
    } catch { toast.error('Export failed'); }
  };

  if (loading) return <DashboardLayout><div className="flex justify-center py-20"><div className="spinner" /></div></DashboardLayout>;
  if (!record) return <DashboardLayout><div className="text-center py-20 text-muted-foreground">Record not found</div></DashboardLayout>;

  const p = record.person || {};
  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'clinical', label: 'Clinical' },
    { id: 'daily', label: 'Daily Living' },
    { id: 'care', label: 'Care Plans' },
    { id: 'team', label: 'Care Team' },
    { id: 'history', label: 'History' },
    { id: 'feedback', label: 'Feedback' },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="client-record-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#00A6A0]/20 flex items-center justify-center text-[#00A6A0] font-bold text-lg">
              {p.name?.charAt(0) || '?'}
            </div>
            <div>
              <h1 className="text-xl font-heading font-bold text-foreground" data-testid="client-name">{p.name}</h1>
              <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                {p.nhs_number && <span className="font-mono bg-muted px-1.5 py-0.5 rounded">NHS: {p.nhs_number}</span>}
                {p.date_of_birth && <span>DOB: {p.date_of_birth}</span>}
                {p.gender && <span className="capitalize">{p.gender}</span>}
                {p.usic && <span className="text-[#FF6F61] font-mono">{p.usic}</span>}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="border-border text-xs" onClick={downloadFHIR} data-testid="export-fhir-btn">
              <Download className="w-3 h-3 mr-1" />FHIR Export
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-muted rounded-lg overflow-x-auto">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`whitespace-nowrap py-2 px-3 rounded-md text-xs font-medium transition-colors ${tab === t.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-testid={`tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* === OVERVIEW === */}
        {tab === 'overview' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Section title="Person Details" icon={User} defaultOpen>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <KV label="Full Name" value={p.name} />
                  <KV label="Preferred Name" value={p.preferred_name} />
                  <KV label="Pronouns" value={p.pronouns} />
                  <KV label="Gender" value={p.gender} />
                  <KV label="Date of Birth" value={p.date_of_birth} />
                  <KV label="Ethnicity" value={p.ethnicity} />
                  <KV label="Religion" value={p.religion} />
                  <KV label="Marital Status" value={p.marital_status} />
                  <KV label="Phone" value={p.phone} />
                  <KV label="Email" value={p.email} />
                </div>
              </Section>
              <Section title="GP Practice" icon={Building2} defaultOpen>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <KV label="Practice" value={record.gp_practice?.name} />
                  <KV label="ODS Code" value={record.gp_practice?.ods_code} />
                  <KV label="Phone" value={record.gp_practice?.phone} />
                  <KV label="Address" value={record.gp_practice?.address} />
                </div>
              </Section>
            </div>

            <Section title="Emergency Contacts & Next of Kin" icon={Phone} count={record.emergency_contacts?.length} defaultOpen>
              {(record.emergency_contacts || []).length === 0 ? <p className="text-xs text-muted-foreground">No contacts recorded</p> : (
                <div className="space-y-2">
                  {record.emergency_contacts.map((c, i) => (
                    <div key={i} className="flex items-center gap-3 bg-muted/30 p-2 rounded-lg text-xs">
                      <Phone className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1">
                        <span className="font-medium text-foreground">{c.name}</span>
                        <span className="text-muted-foreground ml-2">({c.relationship})</span>
                        {c.is_next_of_kin && <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-[#00A6A0]/10 text-[#00A6A0]">NoK</span>}
                        {c.is_lpa && <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-amber-500/10 text-amber-400">LPA</span>}
                      </div>
                      <span className="text-muted-foreground">{c.phone}</span>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section title="Communication Needs (AIS)" icon={FileText}>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <KV label="Language" value={record.communication_needs?.preferred_language} />
                <KV label="Interpreter" value={record.communication_needs?.interpreter_required ? 'Required' : 'Not required'} />
                <KV label="Format" value={record.communication_needs?.format_preference} />
                <KV label="BSL" value={record.communication_needs?.bsl_required ? 'Required' : 'No'} />
                <KV label="Contact Method" value={record.communication_needs?.contact_method_preference} />
                <KV label="Aids" value={record.communication_needs?.communication_aids} />
              </div>
            </Section>
          </div>
        )}

        {/* === CLINICAL === */}
        {tab === 'clinical' && (
          <div className="space-y-4">
            <Section title="Allergies & Adverse Reactions" icon={AlertTriangle} count={record.allergies?.length} defaultOpen>
              {(record.allergies || []).length === 0 ? <p className="text-xs text-muted-foreground">No allergies recorded</p> : (
                <div className="space-y-1">{record.allergies.map(a => (
                  <div key={a.id} className="flex items-center gap-2 bg-red-500/5 border border-red-500/20 p-2 rounded-lg text-xs">
                    <AlertTriangle className="w-3 h-3 text-red-400 flex-shrink-0" />
                    <span className="font-medium text-foreground">{a.substance}</span>
                    <span className="text-muted-foreground">{a.reaction_type}</span>
                    <span className={`text-[10px] px-1 rounded ${a.severity === 'severe' || a.severity === 'life_threatening' ? 'bg-red-500/10 text-red-400' : 'bg-amber-500/10 text-amber-400'}`}>{a.severity}</span>
                  </div>
                ))}</div>
              )}
            </Section>

            <Section title="Medications" icon={Pill} count={record.medications?.length}>
              {(record.medications || []).length === 0 ? <p className="text-xs text-muted-foreground">No medications</p> : (
                <div className="space-y-1">{record.medications.map(m => (
                  <div key={m.id} className="flex items-center justify-between bg-muted/30 p-2 rounded-lg text-xs">
                    <div><span className="font-medium text-foreground">{m.name}</span><span className="text-muted-foreground ml-2">{m.dose} {m.route} {m.frequency}</span></div>
                    <span className={`text-[10px] px-1.5 rounded-full ${m.status === 'active' ? 'bg-[#A8E6CF]/10 text-[#A8E6CF]' : 'bg-muted text-muted-foreground'}`}>{m.status}</span>
                  </div>
                ))}</div>
              )}
            </Section>

            <Section title="Health Observations" icon={Activity} count={record.health_observations?.length}>
              {(record.health_observations || []).length === 0 ? <p className="text-xs text-muted-foreground">No observations</p> : (
                <div className="space-y-2">{record.health_observations.slice(0, 5).map(o => (
                  <div key={o.id} className={`bg-muted/30 p-2 rounded-lg text-xs ${o.has_alerts ? 'border border-red-500/20' : ''}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-muted-foreground">{o.observation_date} - {o.recorded_by_name}</span>
                      {o.has_alerts && <span className="text-[10px] text-red-400">ALERTS</span>}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {Object.entries(o.readings || {}).map(([k, v]) => v != null && (
                        <span key={k} className="bg-muted px-1.5 py-0.5 rounded text-foreground">{k.replace(/_/g, ' ')}: {v}</span>
                      ))}
                    </div>
                  </div>
                ))}</div>
              )}
            </Section>

            <Section title="Consents" icon={Shield} count={record.consents?.length}>
              {(record.consents || []).length === 0 ? <p className="text-xs text-muted-foreground">No consents</p> : (
                <div className="space-y-1">{record.consents.map(c => (
                  <div key={c.id} className="flex items-center justify-between bg-muted/30 p-2 rounded-lg text-xs">
                    <span className="text-foreground">{c.consent_type?.replace(/_/g, ' ')}</span>
                    <span className={`text-[10px] px-1.5 rounded-full ${c.status === 'active' ? 'bg-[#A8E6CF]/10 text-[#A8E6CF]' : 'bg-red-500/10 text-red-400'}`}>{c.status}</span>
                  </div>
                ))}</div>
              )}
            </Section>

            <Section title="Risk Assessments" icon={Shield} count={record.risk_assessments?.length}>
              {(record.risk_assessments || []).length === 0 ? <p className="text-xs text-muted-foreground">No risk assessments</p> : (
                <div className="space-y-1">{record.risk_assessments.map(r => (
                  <div key={r.id} className="flex items-center justify-between bg-muted/30 p-2 rounded-lg text-xs">
                    <span className="text-foreground">{r.assessment_type}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] ${r.risk_level === 'high' ? 'bg-red-500/10 text-red-400' : r.risk_level === 'medium' ? 'bg-amber-500/10 text-amber-400' : 'bg-[#A8E6CF]/10 text-[#A8E6CF]'}`}>{r.risk_level} ({r.total_score})</span>
                  </div>
                ))}</div>
              )}
            </Section>
          </div>
        )}

        {/* === DAILY LIVING === */}
        {tab === 'daily' && (
          <div className="space-y-4" data-testid="daily-living-tab">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Structured daily observations - MODS compliant</p>
              {canWriteDL && (
                <Button size="sm" className="btn-primary" onClick={() => { setDlDialog(true); setDlForm({ category: '', coded_status: '', observation: '', concern_raised: false }); }} data-testid="new-daily-obs-btn">
                  <Plus className="w-4 h-4 mr-1" />Record Observation
                </Button>
              )}
            </div>

            {/* Quick Entry Cards (mobile-optimised) */}
            {canWriteDL && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="dl-quick-cards">
                {Object.entries(DL_LABELS).map(([key, label]) => {
                  const Icon = DL_ICONS[key] || Activity;
                  const latest = (record.daily_living || []).find(d => d.category === key);
                  return (
                    <button key={key} onClick={() => { setDlDialog(true); setDlForm({ category: key, coded_status: '', observation: '', concern_raised: false }); }}
                      className="p-3 rounded-xl bg-muted/30 border border-border hover:border-[#00A6A0]/40 transition-colors text-left" data-testid={`dl-card-${key}`}>
                      <Icon className="w-5 h-5 text-[#00A6A0] mb-1" />
                      <p className="text-xs font-medium text-foreground">{label}</p>
                      {latest ? (
                        <p className="text-[10px] text-muted-foreground mt-0.5">{latest.coded_status?.replace(/_/g, ' ')} - {latest.date}</p>
                      ) : (
                        <p className="text-[10px] text-muted-foreground mt-0.5">No records</p>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Observation History */}
            {(record.daily_living || []).length === 0 ? (
              <div className="text-center py-8"><Activity className="w-10 h-10 text-muted-foreground mx-auto mb-2 opacity-30" /><p className="text-sm text-muted-foreground">No daily living observations recorded</p></div>
            ) : (
              <div className="space-y-2">
                {record.daily_living.map(d => {
                  const Icon = DL_ICONS[d.category] || Activity;
                  return (
                    <div key={d.id} className={`flex items-start gap-3 bg-muted/30 p-3 rounded-lg text-xs ${d.concern_raised ? 'border border-red-500/20' : ''}`}>
                      <Icon className="w-4 h-4 text-[#00A6A0] mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-foreground">{DL_LABELS[d.category] || d.category}</span>
                          <span className="px-1.5 py-0.5 rounded bg-muted text-foreground">{d.coded_status?.replace(/_/g, ' ')}</span>
                          {d.concern_raised && <span className="text-[10px] px-1 rounded bg-red-500/10 text-red-400">CONCERN</span>}
                        </div>
                        {d.observation && <p className="text-muted-foreground mt-1">{d.observation}</p>}
                        <p className="text-muted-foreground mt-1">{d.date} - {d.recorded_by_name}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* === CARE PLANS === */}
        {tab === 'care' && (
          <div className="space-y-4">
            <Section title="Care & Support Plans" icon={ClipboardList} count={record.care_plans?.length} defaultOpen>
              {(record.care_plans || []).length === 0 ? <p className="text-xs text-muted-foreground">No care plans</p> : (
                <div className="space-y-2">{record.care_plans.map(cp => (
                  <Card key={cp.id} className="glass-card border-border">
                    <CardContent className="p-3 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-foreground">{cp.title || cp.name}</span>
                        <span className={`text-[10px] px-1.5 rounded-full ${cp.status === 'active' ? 'bg-[#A8E6CF]/10 text-[#A8E6CF]' : 'bg-muted text-muted-foreground'}`}>{cp.status}</span>
                      </div>
                      {cp.description && <p className="text-muted-foreground mt-1">{cp.description}</p>}
                    </CardContent>
                  </Card>
                ))}</div>
              )}
            </Section>

            <Section title="Safeguarding" icon={Shield} count={record.safeguarding?.length}>
              {(record.safeguarding || []).length === 0 ? <p className="text-xs text-muted-foreground">No safeguarding concerns</p> : (
                <div className="space-y-1">{record.safeguarding.map(s => (
                  <div key={s.id} className="flex items-center justify-between bg-muted/30 p-2 rounded-lg text-xs">
                    <span className="text-red-400">{s.concern_type}</span>
                    <span className={s.status === 'closed' ? 'text-[#A8E6CF]' : 'text-amber-400'}>{s.status}</span>
                  </div>
                ))}</div>
              )}
            </Section>

            <Section title="End-of-Life" icon={Heart}>
              {!record.end_of_life ? <p className="text-xs text-muted-foreground">No end-of-life information recorded</p> : (
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <KV label="Advance Care Plan" value={record.end_of_life.has_advance_care_plan ? 'Yes' : 'No'} />
                  <KV label="DNACPR" value={record.end_of_life.has_dnacpr ? 'Yes' : 'No'} />
                  <KV label="ReSPECT Form" value={record.end_of_life.has_respect_form ? 'Yes' : 'No'} />
                  <KV label="Preferred Place" value={record.end_of_life.preferred_place_of_death?.replace(/_/g, ' ')} />
                  <KV label="Organ Donation" value={record.end_of_life.organ_donation_status?.replace(/_/g, ' ')} />
                </div>
              )}
            </Section>
          </div>
        )}

        {/* === CARE TEAM === */}
        {tab === 'team' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Professional involvement & care team</p>
              {isAdmin && <Button size="sm" className="btn-primary" onClick={() => setProfDialog(true)} data-testid="add-professional-btn"><Plus className="w-4 h-4 mr-1" />Add Professional</Button>}
            </div>
            <Section title="Professionals" icon={Users} count={record.professionals?.length} defaultOpen>
              {(record.professionals || []).length === 0 ? <p className="text-xs text-muted-foreground">No professionals recorded</p> : (
                <div className="space-y-2">{record.professionals.map(pr => (
                  <div key={pr.id} className="flex items-center gap-3 bg-muted/30 p-3 rounded-lg text-xs">
                    <Building2 className="w-4 h-4 text-[#00A6A0] flex-shrink-0" />
                    <div className="flex-1">
                      <span className="font-medium text-foreground">{pr.professional_name}</span>
                      <span className="text-muted-foreground ml-2 capitalize">{pr.role?.replace(/_/g, ' ')}</span>
                      {pr.organization_name && <span className="text-muted-foreground ml-2">({pr.organization_name})</span>}
                    </div>
                    {pr.phone && <span className="text-muted-foreground">{pr.phone}</span>}
                  </div>
                ))}</div>
              )}
            </Section>

            <Section title="Hospital Events" icon={Building2} count={record.hospital_events?.length}>
              {isAdmin && <Button size="sm" variant="outline" className="border-border text-xs mb-2" onClick={() => setHospDialog(true)} data-testid="add-hospital-btn"><Plus className="w-3 h-3 mr-1" />Add Event</Button>}
              {(record.hospital_events || []).length === 0 ? <p className="text-xs text-muted-foreground">No hospital events</p> : (
                <div className="space-y-2">{record.hospital_events.map(h => (
                  <div key={h.id} className="bg-muted/30 p-3 rounded-lg text-xs">
                    <div className="flex items-center gap-2">
                      <span className="capitalize font-medium text-foreground">{h.event_type?.replace(/_/g, ' ')}</span>
                      <span className="text-muted-foreground">{h.hospital_name}</span>
                      <span className="text-muted-foreground">{h.admission_date}</span>
                      {h.discharge_date && <span className="text-[#A8E6CF]">Discharged: {h.discharge_date}</span>}
                    </div>
                    {h.reason && <p className="text-muted-foreground mt-1">{h.reason}</p>}
                  </div>
                ))}</div>
              )}
            </Section>
          </div>
        )}

        {/* === HISTORY === */}
        {tab === 'history' && (
          <div className="space-y-4">
            <Section title="Incidents" icon={AlertTriangle} count={record.incidents?.length} defaultOpen>
              {(record.incidents || []).length === 0 ? <p className="text-xs text-muted-foreground">No incidents</p> : (
                <div className="space-y-1">{record.incidents.map(inc => (
                  <div key={inc.id} className="bg-muted/30 p-2 rounded-lg text-xs">
                    <span className="text-foreground">{inc.type || inc.incident_type || 'Incident'}</span>
                    <span className="text-muted-foreground ml-2">{inc.created_at?.slice(0, 10)}</span>
                    {inc.description && <p className="text-muted-foreground mt-1 line-clamp-2">{inc.description}</p>}
                  </div>
                ))}</div>
              )}
            </Section>
            <Section title="Capacity Assessments" icon={Shield} count={record.capacity_assessments?.length}>
              {(record.capacity_assessments || []).length === 0 ? <p className="text-xs text-muted-foreground">No capacity assessments</p> : (
                <div className="space-y-1">{record.capacity_assessments.map(ca => (
                  <div key={ca.id} className="bg-muted/30 p-2 rounded-lg text-xs">
                    <span className="text-foreground">Decision: {ca.decision || 'General'}</span>
                    <span className="text-muted-foreground ml-2">{ca.assessment_date || ca.created_at?.slice(0, 10)}</span>
                  </div>
                ))}</div>
              )}
            </Section>
          </div>
        )}

        {/* === FEEDBACK === */}
        {tab === 'feedback' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Compliments, suggestions & service feedback</p>
              <Button size="sm" className="btn-primary" onClick={() => setFbDialog(true)} data-testid="add-feedback-btn"><Plus className="w-4 h-4 mr-1" />Add Feedback</Button>
            </div>
            {(record.feedback || []).length === 0 ? (
              <div className="text-center py-8"><Star className="w-10 h-10 text-muted-foreground mx-auto mb-2 opacity-30" /><p className="text-sm text-muted-foreground">No feedback recorded</p></div>
            ) : (
              <div className="space-y-2">{record.feedback.map(fb => (
                <Card key={fb.id} className={`glass-card border-border ${fb.type === 'compliment' ? 'border-[#A8E6CF]/20' : ''}`}>
                  <CardContent className="p-3 text-xs">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-1.5 py-0.5 rounded-full ${fb.type === 'compliment' ? 'bg-[#A8E6CF]/10 text-[#A8E6CF]' : 'bg-amber-500/10 text-amber-400'}`}>{fb.type}</span>
                      <span className="text-muted-foreground">from {fb.source_name || fb.source}</span>
                      <span className="text-muted-foreground">{fb.date_received}</span>
                      {fb.acknowledged && <CheckCircle2 className="w-3 h-3 text-[#A8E6CF]" />}
                    </div>
                    <p className="text-foreground">{fb.description}</p>
                  </CardContent>
                </Card>
              ))}</div>
            )}
          </div>
        )}
      </div>

      {/* Daily Living Dialog */}
      <Dialog open={dlDialog} onOpenChange={setDlDialog}>
        <DialogContent className="bg-background border-border max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Record Daily Observation</DialogTitle><DialogDescription>Select category and coded status per MODS requirements.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(DL_LABELS).map(([key, label]) => {
                const Icon = DL_ICONS[key] || Activity;
                return (
                  <button key={key} onClick={() => setDlForm(f => ({ ...f, category: key, coded_status: '' }))}
                    className={`p-3 rounded-lg border text-left text-xs transition-colors ${dlForm.category === key ? 'border-[#00A6A0] bg-[#00A6A0]/5' : 'border-border hover:border-muted-foreground'}`} data-testid={`dl-select-${key}`}>
                    <Icon className="w-4 h-4 mb-1 text-[#00A6A0]" /><span className="font-medium">{label}</span>
                  </button>
                );
              })}
            </div>
            {dlForm.category && dlCategories[dlForm.category] && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Status:</p>
                <div className="flex flex-wrap gap-1">
                  {dlCategories[dlForm.category].codes.map(code => (
                    <button key={code} onClick={() => setDlForm(f => ({ ...f, coded_status: code }))}
                      className={`px-2 py-1 rounded-md text-xs transition-colors ${dlForm.coded_status === code ? 'bg-[#00A6A0] text-white' : 'bg-muted text-foreground hover:bg-muted/80'}`} data-testid={`dl-code-${code}`}>
                      {code.replace(/_/g, ' ')}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <textarea value={dlForm.observation} onChange={e => setDlForm(f => ({ ...f, observation: e.target.value }))} placeholder="Additional notes (optional)" className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[60px]" />
            <label className="flex items-center gap-2 text-xs text-foreground">
              <input type="checkbox" checked={dlForm.concern_raised} onChange={e => setDlForm(f => ({ ...f, concern_raised: e.target.checked }))} className="rounded" />
              Raise concern
            </label>
            <Button className="w-full btn-primary" onClick={saveDailyLiving} data-testid="save-daily-obs-btn">Save Observation</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Hospital Event Dialog */}
      <Dialog open={hospDialog} onOpenChange={setHospDialog}>
        <DialogContent className="bg-background border-border max-w-md">
          <DialogHeader><DialogTitle>Record Hospital Event</DialogTitle><DialogDescription>Log an admission, discharge, transfer or A&E attendance.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <select value={hospForm.event_type} onChange={e => setHospForm(f => ({ ...f, event_type: e.target.value }))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm">
              <option value="admission">Admission</option><option value="discharge">Discharge</option><option value="transfer">Transfer</option><option value="a_and_e_attendance">A&E Attendance</option>
            </select>
            <Input value={hospForm.hospital_name} onChange={e => setHospForm(f => ({ ...f, hospital_name: e.target.value }))} placeholder="Hospital name" className="bg-muted border-border text-foreground" />
            <Input type="date" value={hospForm.admission_date} onChange={e => setHospForm(f => ({ ...f, admission_date: e.target.value }))} className="bg-muted border-border text-foreground" />
            <Input value={hospForm.reason} onChange={e => setHospForm(f => ({ ...f, reason: e.target.value }))} placeholder="Reason" className="bg-muted border-border text-foreground" />
            <Button className="w-full btn-primary" onClick={saveHospital} data-testid="save-hospital-btn">Save Event</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Professional Dialog */}
      <Dialog open={profDialog} onOpenChange={setProfDialog}>
        <DialogContent className="bg-background border-border max-w-md">
          <DialogHeader><DialogTitle>Add Professional</DialogTitle><DialogDescription>Add a healthcare professional to the care team.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Input value={profForm.professional_name} onChange={e => setProfForm(f => ({ ...f, professional_name: e.target.value }))} placeholder="Full name" className="bg-muted border-border text-foreground" data-testid="prof-name" />
            <select value={profForm.role} onChange={e => setProfForm(f => ({ ...f, role: e.target.value }))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm">
              {['gp','district_nurse','social_worker','occupational_therapist','physiotherapist','speech_therapist','psychiatrist','psychologist','dietitian','podiatrist','pharmacist','community_nurse','care_coordinator','other'].map(r => (
                <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
              ))}
            </select>
            <Input value={profForm.organization_name} onChange={e => setProfForm(f => ({ ...f, organization_name: e.target.value }))} placeholder="Organisation" className="bg-muted border-border text-foreground" />
            <Input value={profForm.phone} onChange={e => setProfForm(f => ({ ...f, phone: e.target.value }))} placeholder="Phone" className="bg-muted border-border text-foreground" />
            <Button className="w-full btn-primary" onClick={saveProfessional} data-testid="save-prof-btn">Add Professional</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Feedback Dialog */}
      <Dialog open={fbDialog} onOpenChange={setFbDialog}>
        <DialogContent className="bg-background border-border max-w-md">
          <DialogHeader><DialogTitle>Record Feedback</DialogTitle><DialogDescription>Log a compliment, suggestion, or general feedback.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <select value={fbForm.type} onChange={e => setFbForm(f => ({ ...f, type: e.target.value }))} className="bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm">
                <option value="compliment">Compliment</option><option value="suggestion">Suggestion</option><option value="general_feedback">General Feedback</option>
              </select>
              <select value={fbForm.source} onChange={e => setFbForm(f => ({ ...f, source: e.target.value }))} className="bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm">
                <option value="client">Client</option><option value="family">Family</option><option value="professional">Professional</option><option value="staff">Staff</option><option value="other">Other</option>
              </select>
            </div>
            <Input value={fbForm.source_name} onChange={e => setFbForm(f => ({ ...f, source_name: e.target.value }))} placeholder="Name of person giving feedback" className="bg-muted border-border text-foreground" />
            <textarea value={fbForm.description} onChange={e => setFbForm(f => ({ ...f, description: e.target.value }))} placeholder="Feedback details..." className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[80px]" />
            <Button className="w-full btn-primary" onClick={saveFeedback} data-testid="save-feedback-btn">Save Feedback</Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <span className="text-muted-foreground">{label}:</span>
      <span className="text-foreground ml-1">{value || '-'}</span>
    </div>
  );
}
